import React from 'react'
import { IonGrid } from '@ionic/react'
import { Collection, CollectionItem } from 'mytours-read-common'
import { MapRoute } from 'mytours-read-api-typescript'

import Header, { Props as HeaderProps } from '../../common/components/header'
import Content from '../../common/components/content'
import Modal from '../../common/components/modal'
import MapLayout from 'mobile_web/modules/map/parts/MapActionsLayout'
import useTourMapModal from '../hooks/useTourMapModal'

interface Props {
	collection: Collection
	collectionItems?: CollectionItem[]
	loading: boolean
	// The stop the reader has open, so the map opens on the same one, and can hand another back.
	openStopId: string | null
	onOpenStopChange: (stopId: string | null) => void
	headerProps: HeaderProps
}

/**
 * The single page tour's header and the map it opens over the page.
 *
 * The two are one feature — a button and the thing it opens — and they are here rather than on the page
 * because `CollectionTour` is a class and cannot hold the hook that owns the modal. Keeping the pair
 * together is what makes that worth a component of its own instead of a wrapper.
 *
 * Addressed by `?tour-map` rather than the `#tour-map` the pager uses: the hash on this layout is the
 * open stop, and a map that took it would collapse the row on the way in.
 */
const SinglePageTourMap: React.FC<Props> = ({
	collection,
	collectionItems,
	loading,
	openStopId,
	onOpenStopChange,
	headerProps,
}) => {
	const hasMap = Boolean(collection?.map_view_enabled && collection?.map)

	const map = useTourMapModal({
		addressBy: 'query',
		hasMap,
		loading,
		isEmpty: !!collectionItems && collectionItems.length === 0,
	})

	// A stop picked off the map opens its row and closes the map. Wired as MapLayout's `onItemClicked`,
	// not `setCurrentPage`: that one also opens the map's own details modal and records it in the URL.
	const openStop = (stop?: CollectionItem) => {
		if (stop) {
			onOpenStopChange(String(stop.item_id))
		}

		map.toggle()
	}

	// The map opens centred on the stop the reader has open, or on the first one when none is.
	const currentItemIndex = Math.max(collectionItems?.findIndex(stop => String(stop.item_id) === openStopId) ?? -1, 0)

	return (
		<>
			<Header {...headerProps} mappable={hasMap} onMapToggle={map.toggle} />
			{hasMap && map.isOpen && (
				<Modal
					isOpen={true}
					cssClass="map-modal"
					backdropDismiss={false}
					showBackdrop={false}
					onDidPresent={() => map.onDidPresent(true)}
					onDidDismiss={() => map.onDidPresent(false)}
				>
					<Content>
						<IonGrid className="map-modal-content">
							{/* Built only once the modal is on screen: the map measures itself on mount, and one
							    built inside a modal that has not been presented measures the wrong box. */}
							{map.didPresent && (
								<MapLayout
									accountId={collection?.account_id}
									details={collection}
									items={collectionItems}
									currentItemIndex={currentItemIndex}
									isMapLayout={false}
									onCloseMap={map.toggle}
									onItemClicked={openStop}
									onSelectItemFromMenu={id => openStop(collectionItems?.find(stop => stop.id === id))}
									keypadEnabled={collection?.keypad_enabled}
									itemType={MapRoute.LinkedTypeEnum.Collection}
								/>
							)}
						</IonGrid>
					</Content>
				</Modal>
			)}
		</>
	)
}

export default SinglePageTourMap
